
import React, { createContext, useState, useEffect, useContext } from 'react';
import axiosInstance from '../utils/api';
import usePiBrowserDetection from '../hooks/usePiBrowserDetection';
import useGeolocation from '../hooks/useGeolocation';

const CurrencyContext = createContext();

// ISO Alpha-2 Country Code to Currency Mapping
const COUNTRY_CODE_TO_CURRENCY_MAP = {
    'NG': { code: 'NGN', symbol: '₦' },
    'GB': { code: 'GBP', symbol: '£' },
    'US': { code: 'USD', symbol: '$' },
    'CA': { code: 'CAD', symbol: 'C$' },
    'DE': { code: 'EUR', symbol: '€' },
    'FR': { code: 'EUR', symbol: '€' },
    'IT': { code: 'EUR', symbol: '€' },
    'ES': { code: 'EUR', symbol: '€' },
    'NL': { code: 'EUR', symbol: '€' },
    'IE': { code: 'EUR', symbol: '€' },
    'GH': { code: 'GHS', symbol: '₵' },
    'KE': { code: 'KES', symbol: 'KSh' },
    'ZA': { code: 'ZAR', symbol: 'R' },
    'IN': { code: 'INR', symbol: '₹' },
    'CN': { code: 'CNY', symbol: '¥' },
    'JP': { code: 'JPY', symbol: '¥' },
    'AU': { code: 'AUD', symbol: 'A$' },
    'NZ': { code: 'NZD', symbol: 'NZ$' },
    'SG': { code: 'SGD', symbol: 'S$' },
    'AE': { code: 'AED', symbol: 'dh' },
};

export const CurrencyProvider = ({ children }) => {
    const { isPiBrowser, isDetecting } = usePiBrowserDetection();
    const { countryCode } = useGeolocation({ enabled: isPiBrowser === false }); // Only enable when we know it's NOT Pi Browser

    const [currency, setCurrency] = useState({ code: 'USD', symbol: '$', rate: 1 });
    const [rates, setRates] = useState({});
    const [ratesLoading, setRatesLoading] = useState(true);

    // Fetch exchange rates on mount
    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await axiosInstance.get('/api/currency/rates');
                if (response.data && Array.isArray(response.data)) {
                    // Convert array to object map if needed, or if API returns object, use it directly.
                    // Our backend `currencyModel.getAllRates` returns array of { currency_code, rate_to_usd, ... }
                    const ratesMap = response.data.reduce((acc, item) => {
                        acc[item.currency_code] = Number(item.rate_to_usd);
                        return acc;
                    }, {});
                    setRates(ratesMap);
                }
            } catch (error) {
                console.error('Failed to fetch currency rates:', error);
            } finally {
                setRatesLoading(false);
            }
        };

        fetchRates();
    }, []);

    // Update active currency based on context
    useEffect(() => {
        if (isPiBrowser) {
            setCurrency({ code: 'Pi', symbol: 'π', rate: 1 });
        } else if (countryCode && COUNTRY_CODE_TO_CURRENCY_MAP[countryCode]) {
            const targetCurrency = COUNTRY_CODE_TO_CURRENCY_MAP[countryCode];
            const rate = rates[targetCurrency.code];

            if (rate) {
                setCurrency({
                    code: targetCurrency.code,
                    symbol: targetCurrency.symbol,
                    rate: rate
                });
            } else {
                // Fallback to USD if rate not found
                setCurrency({ code: 'USD', symbol: '$', rate: 1 });
            }
        } else {
            // Default to USD
            setCurrency({ code: 'USD', symbol: '$', rate: 1 });
        }
    }, [isPiBrowser, countryCode, rates]);

    // Helper to convert USD amount to current currency
    const convertPrice = (amountUsd) => {
        if (typeof amountUsd !== 'number') return 0;
        if (currency.code === 'Pi') return null; // Pi conversion handled separately via price_pi
        return amountUsd * currency.rate;
    };

    // Helper to format price with symbol
    const formatPrice = (amount, includeSymbol = true) => {
        if (amount === null || amount === undefined) return '';
        const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return includeSymbol ? `${currency.symbol}${formatted}` : formatted;
    };

    // Loading is true if rates are loading OR Pi detection is in progress
    const loading = ratesLoading || isDetecting;

    return (
        <CurrencyContext.Provider value={{
            currency,
            rates,
            loading,
            convertPrice,
            formatPrice,
            isPiBrowser: isPiBrowser === true // Explicit boolean, null/undefined becomes false
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => useContext(CurrencyContext);

export default CurrencyProvider;
